<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :width="$vuetify.display.smAndUp ? 340 : 'auto'"
    :z-index="2500"
    v-bind:disabled="isDisabledSelectingNode"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot v-bind:selectedNode="selectedNode"></slot>
      </div>
    </template>

    <v-card v-cloak data-cy="dialog---">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog---">
        {{$t('routes.users.selectFolder')}}

        <v-btn icon @click="cancel()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable" data-cy="dialog------">
        <span v-if="browseNode" class="text-subtitle-1 mb-4 d-block">
          <p>{{$t('routes.users.currentFolder')}}:</p>
          <p><strong>{{browseNode.name}}</strong></p>
        </span>

        <div class="pb-2">
          <FolderCreateInline
            v-if="browseNode"
            :parent-node="browseNode"
            data-cy-prefix="dialog-selectNode"
            @created="handleFolderCreated"
          />
        </div>

        <v-list v-if="nodeList && nodeList.length > 0" v-model:selected="listGroupSelected">
          <div data-cy="dialog-copySelected-listGroup">
            <v-list-item
              v-for="node in nodeList"
              v-bind:key="node.id"
              v-bind:disabled="isNodeDisabled(node)"
              v-on:click="displayNode(node.id)"
              data-cy="dialog-copySelected-listItem">

              <template v-slot:prepend>
                <v-tooltip location="bottom" :z-index="'2501'" open-delay="200">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props">
                      {{ getNodeIconName(node) }}
                    </v-icon>
                  </template>

                  <span>{{getNodeType(node)}}</span>
                </v-tooltip>
              </template>

              <div data-cy="dialog-copySelected-itemContent">
                <v-tooltip location="bottom" :z-index="'2501'" open-delay="200">
                  <template v-slot:activator="{ props }">
                    <v-list-item-title v-bind="props">{{ node.name }}</v-list-item-title>
                  </template>
                  <span>{{node.name}}</span>
                </v-tooltip>
              </div>
            </v-list-item>
          </div>
        </v-list>

        <div v-if="!nodeList || nodeList.length === 0" class="mt-4">
          {{$t('routes.node.noFolders')}}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="browseNode && (canGoBack === null || canGoBack(parentNode))"
          v-on:click="displayNode(browseNode.parentid)"
          variant="text" data-cy="dialog-copySelected-back-btn">
          <v-icon start>mdi-arrow-left</v-icon>
          {{$t('common.back')}}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="submit()" class="bg-primary" variant="text" v-bind:disabled="okDisabled" data-cy="dialog---">{{$t('common.select')}}</v-btn>
        <v-btn variant="text" @click="cancel()" data-cy="dialog---">{{$t('common.cancel')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import FolderCreateInline from '@/components/dialogs/FolderCreateInline.vue';

export default {
  emits: ["update:modelValue"],
  mixins: [DialogActionKeys],
  name: "SelectNodeDialog",
  components: { FolderCreateInline },

  props: {
    modelValue: {
      type: String,
    },
    isDisabledSelectingNode: {
      type: Boolean,
    },
    disabledNodeIds: {
      type: Array,
      required: false,
    },
    isNodeAllowed: {
      type: Function,
      required: false,
      default: null,
    },
    canGoBack: {
      type: Function,
      required: false,
      default: null,
    },
    userType: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      dialog: false,
      errorMessage: '',
      browseNode: null,
      parentNode: null,
      selectedNode: null,
      listGroupSelected: null,
    };
  },

  mounted() {
    if (this.modelValue) {
      this.displayNode(this.modelValue, true);
    }
  },

  watch: {
    dialog(val) {
      if (val) {
        // Dialog opened - reset state if no modelValue
        if (!this.modelValue) {
          this.selectedNode = null;
          this.browseNode = null;
          this.listGroupSelected = null;
        }
      }
    },
    modelValue() {
      // if somebody updates the modelValue from outside
      // then update the selectedNode and browseNode
      this.displayNode(this.modelValue, true);
    }
  },

  methods: {
    cancel() {
      this.dialog = false;
      this.errorMessage = '';
      this.listGroupSelected = null;

      if (this.modelValue) {
        this.displayNode(this.modelValue, true);
        return;
      }

      this.browseNode = null;
      this.parentNode = null;
      this.selectedNode = null;
    },
    async displayNode(nodeID, updateSelectedNode = false) {
      this.listGroupSelected = null;

      // sometimes this.browseNode.parentid is null
      if (nodeID) {
        const nodeStore = useNodeStore();
        this.browseNode = await nodeStore.getNodeInfo(nodeID);
        this.listGroupSelected = null;

        if (this.browseNode && this.browseNode.parentid) {
          this.parentNode = await nodeStore.getNodeInfo(this.browseNode.parentid);
        } else {
          this.parentNode = null;
        }
      } else {
        this.browseNode = null;
        this.parentNode = null;
      }

      if (updateSelectedNode) {
        this.selectedNode = this.browseNode;
      }
    },
    async handleFolderCreated(createdNode) {
      if (!createdNode?.id) {
        return;
      }

      await this.displayNode(createdNode.id);
    },
    submit() {
      if (this.browseNode) {
        this.$emit('update:modelValue', this.browseNode.id);
        this.selectedNode = this.browseNode;
      }

      this.dialog = false;
    },
    isNodeDisabled(node) {
      if (node.teamonly === 1 && this.isClient || !node.rootpath.startsWith(`/${this.accessPointId}`)) {
        return true;
      }

      if (this.isNodeAllowed !== null && !this.isNodeAllowed(node)) {
        return true;
      }

      if (!this.disabledNodeIds || Array.isArray(this.disabledNodeIds) && this.disabledNodeIds.length === 0) {
        return false;
      }

      return this.disabledNodeIds.indexOf(node.id) >= 0;
    },
  },

  computed: {
    nodeList() {
      if (this.browseNode) return this.browseNode.children || [];
      return this.accessPoints || [];
    },
    accessPoints() {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    accessPointId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    getNodeType() {
      const commonStore = useCommonStore();
      const TYPES = commonStore.nodeTypes;

      const self = this;
      return (node) => {
        switch (node.type) {
          case TYPES.FOLDER: return self.$t('routes.node.typeFolder');
          case TYPES.PROJECT: return self.$t('routes.node.typeProject');
          case TYPES.CLIENT: return self.$t('routes.node.typeClient');
          case TYPES.TENANT: return self.$t('routes.node.typeTenant');
          default: return self.$t('routes.node.typeUnknown');
        }
      };
    },
    getNodeIconName() {
      return (node) => {
        let iconName;
        switch (node.type) {
          case 2: iconName = "mdi-briefcase-clock-outline"; break;
          case 3: iconName = "tw tw-office-building"; break;
          case 4: iconName = "mdi-home-assistant"; break;
          default: iconName = "mdi-folder-outline"; break;
        }
        return iconName;
      };
    },
    okDisabled() {
      return !this.browseNode;
    },
    isClient() {
      return this.userType === 'client';
    },
    isTeamMember() {
      return this.userType === 'teamMember';
    },
  }
};
</script>
